import React, { useEffect, useRef } from "react";
import HumanMessageBubble from "./HumanMessageBubble";
import AssistantMessageBubble from "./AssistantMessageBubble";
import { shouldRenderMessage, getContentString } from "./chatUtils";
import useIsMobile from "../../hooks/useIsMobile";

const DecisionChip = ({ decision }) => {
  const isApprove = decision.type === "approve";
  const borderColor = isApprove ? "#16a34a" : "#dc2626";
  const textColor = isApprove ? "#16a34a" : "#dc2626";
  const bgColor = isApprove ? "rgba(22,163,74,0.06)" : "rgba(220,38,38,0.06)";

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        marginBottom: "12px",
      }}
    >
      <div
        style={{
          padding: "8px 16px",
          borderRadius: "16px",
          border: `2px solid ${borderColor}`,
          backgroundColor: bgColor,
          color: textColor,
          fontSize: "13px",
          fontWeight: 600,
        }}
      >
        {decision.content}
      </div>
    </div>
  );
};

const ChatMessages = ({ messages, isLoading, decisions = [], feedback = {}, onFeedback }) => {
  const isMobile = useIsMobile();
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, decisions]);

  const filtered = (messages || []).filter(
    (m) => shouldRenderMessage(m) && (m.type === "human" || m.type === "ai")
  );

  if (filtered.length === 0) {
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#999",
          fontSize: "15px",
        }}
      >
        Send a message to start chatting
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        overflowY: "auto",
        overscrollBehavior: "contain",
        WebkitOverflowScrolling: "touch",
        padding: isMobile ? "12px 12px 8px" : "24px 24px 8px",
      }}
    >
      {filtered.map((msg, idx) => {
        const bubble =
          msg.type === "human" ? (
            <HumanMessageBubble key={msg.id || idx} message={msg} />
          ) : (
            <AssistantMessageBubble
              key={msg.id || idx}
              message={msg}
              feedback={feedback[msg.id]}
              onFeedback={onFeedback ? (rating) => onFeedback(msg.id, rating) : undefined}
            />
          );

        const matchingDecisions = decisions.filter(
          (d) => d.afterMessageId === msg.id
        );

        return (
          <React.Fragment key={msg.id || idx}>
            {bubble}
            {matchingDecisions.map((d) => (
              <DecisionChip key={d.id} decision={d} />
            ))}
          </React.Fragment>
        );
      })}
      {isLoading && (() => {
        const last = filtered[filtered.length - 1];
        return !last || last.type !== "ai" || (!getContentString(last) && (!last.tool_calls || last.tool_calls.length === 0));
      })() && (
        <div style={{ display: "flex", gap: "10px", marginBottom: "12px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: "#825ee7",
              flexShrink: 0,
            }}
          />
          <div
            style={{
              padding: "12px 16px",
              borderRadius: "4px 16px 16px 16px",
              backgroundColor: "#fff",
              border: "1px solid #e8e8e8",
              color: "#999",
              fontSize: "14px",
            }}
          >
            Thinking...
          </div>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
};

export default ChatMessages;
